import React, { useEffect, useState } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { Dialog } from '@mui/material';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'FileViewerModal', level: 'info' });

interface FileViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string | null;
  mimeType: string;
}

const FileViewerModal: React.FC<FileViewerModalProps> = ({ isOpen, onClose, fileUrl, mimeType }) => {
  const { darkMode } = useDarkMode();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [viewerError, setViewerError] = useState(false);

  // Convert external URL to blob URL to avoid CSP issues
  useEffect(() => {
    let isMounted = true;
    
    const fetchAndCreateBlobUrl = async () => {
      if (!fileUrl || !isOpen) return;
      
      setIsLoading(true);
      setLoadError(null);
      setViewerError(false);
      
      try {
        // Check if the URL is from a domain that might cause CSP issues
        const url = new URL(fileUrl);
        const potentialCspIssue = ['drive.google.com', 'docs.google.com'].some(
          domain => url.hostname.includes(domain)
        );
        
        if (potentialCspIssue) {
          throw new Error('Content Security Policy restricts loading content from Google Drive directly.');
        }
        
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        
        if (isMounted) {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
          setIsLoading(false);
        }
      } catch (error) {
        logger.error('Error creating blob URL:', error);
        if (isMounted) {
          if (error instanceof Error && error.message.includes('Content Security Policy')) {
            setLoadError('This file cannot be displayed due to browser security restrictions. Please use the download or open in new tab options.');
          } else {
            setLoadError('Failed to load the file. Please try downloading it instead.');
          }
          setIsLoading(false);
        }
      }
    };
    
    fetchAndCreateBlobUrl();
    
    // Cleanup function
    return () => {
      isMounted = false;
      // Revoke the blob URL to avoid memory leaks
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    };
  }, [fileUrl, isOpen, mimeType]);

  // Handle errors from object tag
  const handleViewerError = () => {
    console.error('PDF viewer error - falling back to download option');
    setViewerError(true);
    logger.warn('PDF viewer failed to load, showing fallback download option');
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !fileUrl) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 overflow-y-auto"
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center'
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        },
        '& .MuiDialog-paper': {
          margin: 0,
          maxWidth: 'none',
          width: '95vw',
          height: '95vh',
          backgroundColor: darkMode ? '#1f2937' : 'white',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg w-full h-full flex flex-col`}>
        <div className="flex justify-between items-center p-4">
          <h2 className="text-xl font-bold">File Preview</h2>
          <button 
            onClick={onClose} 
            className={`${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-500 hover:text-gray-700'} text-2xl`}
          >
            ✕
          </button>
        </div>
        
        <div className="flex-1 p-4 overflow-hidden" style={{ height: 'calc(100% - 80px)' }}>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
              <p>Loading file...</p>
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center h-full">
              <IoDocumentTextOutline className="w-16 h-16 text-gray-400 mb-4" />
              <p className="mb-4 text-center text-red-500">{loadError}</p>
              <div className="flex space-x-4">
                <a
                  href={fileUrl}
                  download
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                >
                  Download File
                </a>
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
                >
                  Open in New Tab
                </a>
              </div>
            </div>
          ) : mimeType === 'application/pdf' && blobUrl && !viewerError ? (
            <div className="h-full w-full">
              <iframe
                src={blobUrl}
                className="w-full h-full border-0"
                style={{ minHeight: '600px' }}
                title="PDF Viewer"
              />
              <div className="mt-2 text-center">
                <p className="text-sm text-gray-500">
                  If the PDF doesn't display correctly, you can 
                  <a 
                    href={blobUrl} 
                    download="document.pdf"
                    className="text-blue-500 hover:text-blue-700 mx-1"
                  >
                    download it
                  </a>
                  or
                  <a 
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 mx-1"
                  >
                    open in new tab
                  </a>.
                </p>
              </div>
            </div>
          ) : mimeType === 'application/pdf' && blobUrl && viewerError ? (
            <div className="flex flex-col items-center justify-center h-full">
              <IoDocumentTextOutline className="w-16 h-16 text-gray-400 mb-4" />
              <p className="mb-4 text-center">
                Unable to display PDF file due to browser security restrictions.
              </p>
              <div className="flex space-x-4">
                <a
                  href={blobUrl}
                  download="document.pdf"
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                >
                  Download PDF
                </a>
                <a
                  href={blobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
                >
                  Open in New Tab
                </a>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <IoDocumentTextOutline className="w-16 h-16 text-gray-400 mb-4" />
              <p className="mb-4 text-center">
                {blobUrl ? 
                  `This file format (${mimeType}) cannot be displayed directly in the browser.` :
                  'Preparing file for display...'}
              </p>
              {blobUrl && (
                <div className="flex space-x-4">
                  <a
                    href={blobUrl}
                    download
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                  >
                    Download File
                  </a>
                  <a
                    href={blobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
                  >
                    Open in New Tab
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export default FileViewerModal; 
